/**
 * @fileoverview Own destination browsing/selection for workspace-requested file operations. Caller-supplied hierarchy and exclusion rules shape valid choices; the parent owns final move/copy execution. `DocumentFileExplorerController.js` supplies common explorer interaction. Choosing a destination does not itself change canonical location or establish write access.
 */

import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import DocumentFileTypeIcon from './DocumentFileTypeIcon';
import { normalizeDocumentFolderPath } from './DocumentFileExplorerController';
import './FolderDestinationModal.css';



export function folderDestinationKey(folder) {
  const folderId = String(folder?.folder_id || '').trim();
  if (folderId) return `id:${folderId}`;
  return `path:${String(folder?.matter_number || '').trim().toLowerCase()}:${folderDestinationPath(folder).toLowerCase()}`;
}



export function folderDestinationPath(folder) {
  return folder?.folder_type === 'matter' ? '' : normalizeDocumentFolderPath(folder?.folder_path);
}



export function folderDestinationLabel(folder, folders = []) {
  const matter = String(folder?.matter_number || '').trim();
  const path = folderDestinationPath(folder);
  if (matter && folderDestinationScope(folder, folders) === 'matter') {
    return path ? `Matter ${matter} / ${path}` : `Matter ${matter}`;
  }
  return path ? `Internal / ${path}` : 'Internal documents';
}



function folderCaseInfo(folder) {
  return folder?.case_info && typeof folder.case_info === 'object' ? folder.case_info : {};
}



export function folderDestinationCaseSummary(folder) {
  const caseInfo = folderCaseInfo(folder);
  const clients = (caseInfo.client_names || []).filter(Boolean).join(', ');
  const defendants = (caseInfo.defendant_names || []).filter(Boolean).join(', ');
  const parties = clients && defendants ? `${clients} v. ${defendants}` : clients || defendants;
  const location = caseInfo.jurisdiction || caseInfo.state || '';
  const caseNumber = caseInfo.case_number ? `Case ${caseInfo.case_number}` : '';
  return [parties, location, caseNumber].filter(Boolean).join(' · ');
}



export function folderDestinationSearchText(folder, folders = []) {
  const caseInfo = folderCaseInfo(folder);
  return [
    folderDestinationLabel(folder, folders),
    folder?.name,
    folder?.source_simul_folder_path,
    ...(folder?.case_tags || []),
    ...(caseInfo.client_names || []),
    ...(caseInfo.defendant_names || []),
    ...(caseInfo.attorney_names || []),
    caseInfo.jurisdiction,
    caseInfo.state,
    caseInfo.case_number,
    caseInfo.calendar_title,
  ].filter(Boolean).join(' ').toLowerCase();
}



export function folderDestinationScope(folder, folders = []) {
  if (folder?.folder_type === 'matter') return 'matter';
  if (folders.length === 0) {
    return String(folder?.root_kind || '').trim().toLowerCase() === 'matter' ? 'matter' : 'internal';
  }
  const byId = new Map(folders.map((candidate) => [String(candidate?.folder_id || ''), candidate]));
  const visited = new Set();
  let parentId = String(folder?.parent_folder_id || '');
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) return 'internal';
    if (parent.folder_type === 'matter') return 'matter';
    parentId = String(parent.parent_folder_id || '');
  }
  return 'internal';
}



export function folderMovePayload(folder) {
  return {
    destination_folder_id: String(folder?.folder_id || '').trim(),
    matter_number: String(folder?.matter_number || '').trim(),
    folder_path: folderDestinationPath(folder),
  };
}



export function folderCreatePayload(parentFolder, folderName) {
  const parentPath = folderDestinationPath(parentFolder);
  const parentFolderId = String(parentFolder?.folder_id || '').trim();
  const normalizedFolderName = normalizeDocumentFolderPath(folderName);
  return {
    matter_number: String(parentFolder?.matter_number || '').trim(),
    folder_path: normalizeDocumentFolderPath([
      parentPath,
      normalizedFolderName,
    ].filter(Boolean).join('/')),
    ...(parentFolderId ? {
      parent_folder_id: parentFolderId,
      folder_name: normalizedFolderName,
    } : {}),
  };
}



function FolderDestinationModal(props) {
  // The catalog can contain thousands of folders. A closed chooser must not
  // build/sort destinations on unrelated workspace renders (including tabs).
  return props.visible ? <FolderDestinationModalContent {...props} /> : null;
}



function FolderDestinationModalContent({
  visible,
  itemType = 'file',
  itemCount = 1,
  action = 'move',
  itemLabel,
  folders = [],
  excludedFolderIds = [],
  excludedDestinationKeys = [],
  requireFolderId = false,
  defaultScope = 'all',
  preserveSimulLocation = false,
  submitting = false,
  error = '',
  defaultCreateParent = null,
  onClose,
  onCreateFolder,
  onSubmit,
}) {
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [createdFolders, setCreatedFolders] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [conflictPolicy, setConflictPolicy] = useState('keep_both');
  const [scope, setScope] = useState(defaultScope);

  useEffect(

  () => {
    if (!visible) return;
    setQuery('');
    setSelectedKey('');
    setCreatedFolders([]);
    setCreateOpen(false);
    setFolderName('');
    setCreating(false);
    setCreateError('');
    setConflictPolicy('keep_both');
    setScope(defaultScope);
  }, [action, defaultScope, itemLabel, itemType, visible]);
  const isMatterMerge = action === 'merge';
  const actionLabel = action === 'copy' ? 'Copy' : isMatterMerge ? 'Merge with matter' : 'Move';
  const itemNoun = itemType === 'file' && Number(itemCount) !== 1
    ? 'documents'
    : itemType;

  const excludedIds = useMemo(
    () => new Set(excludedFolderIds.map((value) => String(value || '').trim()).filter(Boolean)),
    [excludedFolderIds]
  );
  const excludedKeys = useMemo(
    () => new Set(excludedDestinationKeys),
    [excludedDestinationKeys]
  );
  const destinationCatalog = useMemo(
    () => [...folders, ...createdFolders],
    [createdFolders, folders]
  );
  const destinations = useMemo(() => {
    const unique = new Map();
    destinationCatalog.forEach(

    (folder) => {
      const folderId = String(folder?.folder_id || '').trim();
      if (!folder || folder.folder_type === 'simul') return;
      if (isMatterMerge && (
        folder.folder_type !== 'matter' || String(folder.parent_folder_id || '').trim()
      )) return;
      if (requireFolderId && !folderId) return;
      if (folderId && excludedIds.has(folderId)) return;
      const key = folderDestinationKey(folder);
      if (excludedKeys.has(key)) return;
      unique.set(key, folder);
    });
    return [...unique.values()].sort((left, right) => (
      folderDestinationLabel(left, destinationCatalog).localeCompare(folderDestinationLabel(right, destinationCatalog))
    ));
  }, [destinationCatalog, excludedIds, excludedKeys, isMatterMerge, requireFolderId]);
  const normalizedQuery = query.trim().toLowerCase();
  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const matches = destinations.filter(

  (folder) => {
    if (scope !== 'all' && folderDestinationScope(folder, destinationCatalog) !== scope) return false;
    if (!normalizedQuery) return true;
    const searchable = folderDestinationSearchText(folder, destinationCatalog);
    return queryTokens.every((token) => searchable.includes(token));
  });
  const visibleMatches = matches.slice(0, 250);
  const selectedFolder = destinations.find((folder) => folderDestinationKey(folder) === selectedKey) || null;
  const createParent = selectedFolder || defaultCreateParent || null;
  const canCreateFolder = itemType === 'file' && typeof onCreateFolder === 'function';
  const normalizedFolderName = normalizeDocumentFolderPath(folderName);
  const createPayload = folderCreatePayload(createParent, folderName);
  const createDisabled = creating || !normalizedFolderName;
  const scopeLabel = scope === 'matter' ? 'matter folders' : scope === 'internal' ? 'internal folders' : 'folders';
  const searchPlaceholder = scope === 'matter'
    ? 'Matter, client, defendant, or case number'
    : scope === 'internal'
    ? 'Folder name or path'
    : 'Matter, client, defendant, case number, or folder';

  const selectScope =

  (nextScope) => {
    setScope(nextScope);
    setSelectedKey('');
  };

  const openCreateFolder =

  () => {
    setFolderName(normalizeDocumentFolderPath(query));
    setCreateError('');
    setCreateOpen(true);
  };

  const submitCreateFolder =

  async () => {
    if (createDisabled) return;
    setCreating(true);
    setCreateError('');
    try {
      const created = await onCreateFolder(createPayload);
      if (!created) throw new Error('The folder was created, but its destination could not be loaded.');
      const destination = {
        ...created,
        folder_type: created.folder_type || 'location',
        parent_folder_id: created.parent_folder_id ?? createPayload.parent_folder_id,
        matter_number: created.matter_number ?? createPayload.matter_number,
        folder_path: created.folder_path || createPayload.folder_path,
      };
      setCreatedFolders((current) => [...current, destination]);
      setSelectedKey(folderDestinationKey(destination));
      setQuery('');
      setFolderName('');
      setCreateOpen(false);
    } catch (requestError) {
      setCreateError(
        requestError?.response?.data?.error || requestError.message || 'Could not create folder.'
      );
    } finally {
      setCreating(false);
    }
  };

  if (!visible) return null;
  return (
    <div
      className="folder-destination-modal__backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting && !creating) onClose();
      }}>
      <section
        className="folder-destination-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="folder-destination-modal-title">
        <header>
          <span className="folder-destination-modal__heading-icon" aria-hidden="true">
            {itemType === 'folder'
              ? <AppIcon icon="pi pi-folder" />
              : <DocumentFileTypeIcon file={itemLabel} />}
          </span>
          <span>
            <h2 id="folder-destination-modal-title">{actionLabel} {itemNoun}</h2>
            <small>{itemLabel || `Selected ${itemType}`}</small>
          </span>
          <button type="button" aria-label={`Close ${action} ${itemNoun}`} disabled={submitting || creating} onClick={onClose}>
            <AppIcon icon="pi pi-times" />
          </button>
        </header>

        <div className="folder-destination-modal__toolbar">
          <strong>Choose destination</strong>
          {canCreateFolder && (
            <button
              type="button"
              aria-expanded={createOpen}
              disabled={submitting || creating}
              onClick={createOpen ? () => setCreateOpen(false) : openCreateFolder}>
              <AppIcon icon={createOpen ? 'pi pi-times' : 'pi pi-folder-plus'} aria-hidden="true" />
              {createOpen ? 'Cancel new folder' : 'New folder'}
            </button>
          )}
        </div>

        <label className="folder-destination-modal__search">
          {!isMatterMerge && <span className="folder-destination-modal__scopes" role="group" aria-label="Destination type">
            <button
              type="button"
              className={scope === 'matter' ? 'is-selected' : ''}
              aria-pressed={scope === 'matter'}
              disabled={submitting || creating}
              onClick={() => selectScope('matter')}>
              <AppIcon icon="pi pi-briefcase" aria-hidden="true" />
              Matter folders
            </button>
            <button
              type="button"
              className={scope === 'internal' ? 'is-selected' : ''}
              aria-pressed={scope === 'internal'}
              disabled={submitting || creating}
              onClick={() => selectScope('internal')}>
              <AppIcon icon="pi pi-folder" aria-hidden="true" />
              Internal folders
            </button>
            <button
              type="button"
              className={scope === 'all' ? 'is-selected' : ''}
              aria-pressed={scope === 'all'}
              disabled={submitting || creating}
              onClick={() => selectScope('all')}>
              All
            </button>
          </span>}
          <span>
            <AppIcon icon="pi pi-search" aria-hidden="true" />
            <input
              type="search"
              aria-label="Search folder destinations"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              disabled={submitting || creating}
              autoFocus={!createOpen} />
          </span>
        </label>

        {createOpen && (
          <section className="folder-destination-modal__create" aria-label="Create destination folder">
            <div className="folder-destination-modal__create-parent">
              <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
              <span>
                <small>Inside</small>
                <strong>{folderDestinationLabel(createParent, destinationCatalog)}</strong>
              </span>
            </div>
            <label>
              <span>Folder name</span>
              <input
                value={folderName}
                disabled={creating}
                placeholder="e.g. Motions"
                autoFocus
                onChange={(event) => setFolderName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitCreateFolder();
                  }
                }} />
            </label>
            {createError && <div className="folder-destination-modal__error" role="alert">{createError}</div>}
            <button type="button" disabled={createDisabled} onClick={submitCreateFolder}>
              <AppIcon icon={creating ? 'pi pi-spin pi-spinner' : 'pi pi-folder-plus'} aria-hidden="true" />
              {creating ? 'Creating…' : 'Create and select'}
            </button>
          </section>
        )}

        <div className="folder-destination-modal__results" role="listbox" aria-label="Folder destinations">
          {visibleMatches.length > 0 ? visibleMatches.map(

          (folder) => {
            const key = folderDestinationKey(folder);
            const selected = selectedKey === key;
            const isMatterDestination = folderDestinationScope(folder, destinationCatalog) === 'matter';
            const caseSummary = isMatterDestination ? folderDestinationCaseSummary(folder) : '';
            return (
              <button
                type="button"
                role="option"
                aria-selected={selected}
                key={key}
                className={selected ? 'is-selected' : ''}
                disabled={submitting || creating}
                onClick={() => setSelectedKey(key)}>
                <span aria-hidden="true"><AppIcon icon={folder.folder_type === 'matter' ? 'pi pi-briefcase' : 'pi pi-folder'} /></span>
                <span>
                  <strong>{folder?.name || folderDestinationLabel(folder, destinationCatalog)}</strong>
                  <small>{folderDestinationLabel(folder, destinationCatalog)}</small>
                  {caseSummary && <span className="folder-destination-modal__case-info">{caseSummary}</span>}
                </span>
                <AppIcon icon={selected ? 'pi pi-check-circle' : 'pi pi-circle'} aria-hidden="true" />
              </button>
            );
          }) : (
            <div className="folder-destination-modal__empty">No {scopeLabel} match this search.</div>
          )}
        </div>
        {matches.length > visibleMatches.length && (
          <p className="folder-destination-modal__limit">Showing the first 250 matches. Search to narrow the list.</p>
        )}

        {selectedFolder && (
          <div className="folder-destination-modal__selection" role="status">
            <AppIcon icon="pi pi-arrow-right" aria-hidden="true" />
            <span>
              <small>{isMatterMerge ? 'Merge into' : `${actionLabel} to`}</small>
              <strong>{folderDestinationLabel(selectedFolder, destinationCatalog)}</strong>
            </span>
          </div>
        )}
        {!isMatterMerge && <label className="folder-destination-modal__conflict">
          <span>If a name already exists</span>
          <select value={conflictPolicy} disabled={submitting || creating} onChange={(event) => setConflictPolicy(event.target.value)}>
            <option value="keep_both">Keep both</option>
            <option value="replace">Replace existing</option>
            <option value="skip">Skip this item</option>
            <option value="cancel">Cancel the whole action</option>
          </select>
        </label>}
        {isMatterMerge && (
          <div className="folder-destination-modal__provenance" role="note">
            <AppIcon icon="pi pi-sitemap" aria-hidden="true" />
            <span>
              Matching subfolders will be combined. Files keep their names
              {preserveSimulLocation ? ', and Simul source history stays attached' : ''}.
            </span>
          </div>
        )}
        {preserveSimulLocation && !isMatterMerge && (
          <div className="folder-destination-modal__provenance" role="note">
            <AppIcon icon="pi pi-history" aria-hidden="true" />
            <span>The original Simul location will remain attached as source history.</span>
          </div>
        )}
        {error && <div className="folder-destination-modal__error" role="alert">{error}</div>}

        <footer>
          <button type="button" className="is-secondary" disabled={submitting || creating} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="is-primary"
            disabled={!selectedFolder || submitting || creating}
            onClick={() => onSubmit(selectedFolder, isMatterMerge ? 'keep_both' : conflictPolicy)}>
            {submitting ? <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" /> : <AppIcon icon={isMatterMerge ? 'pi pi-briefcase' : 'pi pi-folder'} aria-hidden="true" />}
            {submitting
              ? (action === 'copy' ? 'Copying…' : isMatterMerge ? 'Merging…' : 'Moving…')
              : `${actionLabel} ${itemNoun}`}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default FolderDestinationModal;
